<template>
    <ClientOnly>
        <MenuBar v-if="editor" :editor="editor" @edit-mode-active="toggleEditMode" @content-saved="save" @content-published="publish" />

        <div class="content_styles" :class="{editor__frame: isEditMode}">
            <EditorContent :editor="editor" />
        </div>
    </ClientOnly>
</template>

<script setup lang="ts">
    import {onBeforeUnmount, onMounted, ref, type Ref} from 'vue';
    import {Image} from '@tiptap/extension-image';
    import {Link} from '@tiptap/extension-link';
    import {Table} from '@tiptap/extension-table';
    import {TableCell} from '@tiptap/extension-table-cell';
    import {TableHeader} from '@tiptap/extension-table-header';
    import {TableRow} from '@tiptap/extension-table-row';
    import {TextAlign} from '@tiptap/extension-text-align';
    import {Underline} from '@tiptap/extension-underline';
    import {StarterKit} from '@tiptap/starter-kit';
    import {Editor, EditorContent} from '@tiptap/vue-3';
    import {ClientOnly} from '#components';
    import {useLocale, useUniversalPost} from '#imports';
    import {pushVJoySnackbar} from '@maltjoy/core-vue';
    import MenuBar from './MenuBar.vue';

    interface Props {
        content: string;
        humanId: string;
        type: string;
    }

    const {content, humanId, type} = defineProps<Props>();

    const {locale} = useLocale();
    const {editor} = manageEditor(content);
    const {isEditMode, toggleEditMode} = manageEditMode(editor);
    const {save, publish} = manageContentPersistence({editor, humanId, type, locale});

    function manageEditor(content: string) {
        const editor = ref<Editor | undefined>();

        onBeforeUnmount(destroyEditor);
        onMounted(createEditor);

        function getEditorOptions() {
            return {
                editable: false,
                content,
                extensions: [
                    Image,
                    Table,
                    TableRow,
                    TextAlign.configure({
                        types: ['heading', 'paragraph'],
                    }),
                    TableHeader,
                    TableCell,
                    Underline,
                    Link.configure({
                        openOnClick: false,
                    }),
                    StarterKit.configure({
                        heading: {
                            levels: [1, 2, 3, 4],
                        },
                    }),
                ],
            };
        }

        function createEditor() {
            if (editor.value) {
                return;
            }
            editor.value = new Editor(getEditorOptions());
        }

        function destroyEditor() {
            editor.value?.destroy();
            editor.value = undefined;
        }

        return {
            editor,
            createEditor,
            destroyEditor,
        };
    }

    function manageEditMode(editor: Ref<Editor | undefined>) {
        const isEditMode = ref(false);

        function toggleEditMode(editMode: boolean) {
            isEditMode.value = editMode;
            editor.value?.setEditable(editMode);
        }

        return {
            isEditMode,
            toggleEditMode,
        };
    }

    function manageContentPersistence({
        editor,
        humanId,
        type,
        locale,
    }: {
        editor: Ref<Editor | undefined>;
        humanId: string;
        type: string;
        locale: Ref<string>;
    }) {
        async function save() {
            try {
                await useUniversalPost('/api/content', {
                    query: {
                        humanId,
                        type,
                        locale: locale.value,
                    },
                    body: editor.value?.getHTML(),
                });
                pushVJoySnackbar({props: {message: 'Content saved!'}});
            } catch (_) {
                pushVJoySnackbar({props: {message: 'Content could not be saved!', level: 'error'}});
            }
        }

        async function publish() {
            try {
                await useUniversalPost('/api/content/versions', {
                    query: {
                        humanId,
                        type,
                        locale: locale.value,
                    },
                    body: editor.value?.getHTML(),
                });
                pushVJoySnackbar({props: {message: 'Content published!'}});
            } catch (_) {
                pushVJoySnackbar({props: {message: 'Content could not be published!', level: 'error'}});
            }
        }

        return {
            save,
            publish,
        };
    }
</script>

<style lang="scss">
    .editor {
        background-color: var(--joy-color-neutral-0);
        border: 3px solid var(--joy-color-neutral-70);
        border-radius: var(--joy-core-radius-3);
        color: var(--joy-color-text-title);
        display: flex;
        flex-direction: column;
        max-height: 26rem;

        &__frame {
            border: 1px solid var(--joy-color-neutral-30);
            border-radius: var(--joy-core-radius-3);
            padding: var(--joy-core-spacing-2);
        }
    }
</style>
